import {
  FontSizeOutlined,
  StarOutlined,
} from '@ant-design/icons';
import {
  AppUserMenu,
  AppUserMenuAction,
  IconLogout,
  ScopeButton,
  USER_MENU_ICON_STYLE,
} from '@monorepo/shared-lib';
import { Progress, Spin } from 'antd';
import { ReactComponent as IconBilling } from 'assets/images/icons/iconBilling.svg';
import { ReactComponent as IconUsers } from 'assets/images/icons/iconUsers.svg';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routes from 'routes';
import { AccountType } from 'store/api/enums';
import { LicenseStatus, LicenseType, MenuSize } from 'store/app/enums';
import { getProfilePictures } from 'store/app/selectors';
import { logoutRequest } from 'store/auth/actions';
import { UserLicence } from 'store/auth/models';
import { getAuthLoading, getAuthUser } from 'store/auth/selectors';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { isMultipleLanguagesDisabled } from 'utils/feature-flag-utils';
import { useReviewModal } from 'utils/use-review-modal';
import { LanguageSelector } from './LanguageSelector';
import { ColoredTag } from './spans';
import LogoSmall from 'assets/images/gt-logo-menu-small.svg';

const antIconStyle: React.CSSProperties = {
  color: 'var(--color-text-dark, #042a2a)',
  fontSize: 18,
  margin: 0,
};

interface UserMenuProps {
  menuSize: MenuSize;
  userLicence?: UserLicence;
  /** Static brand mark for PWA header (non-interactive). */
  compactHeader?: boolean;
  /** Avatar-only chip in the PWA header (no sidebar spacer). */
  embedInHeader?: boolean;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  menuSize,
  userLicence,
  compactHeader = false,
  embedInHeader = false,
}) => {
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const multipleLanguagesDisabled = isMultipleLanguagesDisabled();
  const isNarrow = menuSize === MenuSize.NARROW;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector(getAuthUser);
  const userLoading = useSelector(getAuthLoading);
  const profilePictures = useSelector(getProfilePictures);
  const userName = currentUser?.firstName ?? '';
  const { showReviewModal } = useReviewModal();

  if (compactHeader) {
    return (
      <div
        className="user-menu-button-small user-menu-button-small--brand"
        aria-hidden="true"
      >
        <img
          className="user-menu-button-small__logo"
          src={LogoSmall}
          alt=""
        />
      </div>
    );
  }

  const getProfilePictureForUser = () => {
    if (currentUser?.profilePicture) {
      return currentUser.profilePicture;
    }
    if (currentUser?.userUuid && profilePictures) {
      const profilePic = profilePictures.find(
        (pp) => pp?.userUuid === currentUser.userUuid
      );
      return profilePic?.profilePicture;
    }
    return undefined;
  };

  const userProfilePicture = getProfilePictureForUser();

  const renderUserSubscription = () => {
    let tagKey = 'navigation.subscription-subscribed';
    let tagConfig = {
      bgcolor: '#AF41AA',
      padding: isNarrow ? '2px 2px' : '2px 6px',
      fontSize: 10,
      fontWeight: 600,
      marginBottom: 0,
    };

    if (userLicence?.licenseType === LicenseType.TRIAL) {
      if (userLicence?.licenseDaysLeft === 0) {
        tagConfig = { ...tagConfig, bgcolor: '#D13131' };
        tagKey = 'navigation.subscription-trial-expired';
      } else {
        tagKey = 'navigation.subscription-free-trial';
      }
    } else if (userLicence?.licenseType === LicenseType.PAID) {
      if (userLicence?.licenseDaysLeft <= 0) {
        tagConfig = { ...tagConfig, bgcolor: '#FA8C16' };
        tagKey = 'navigation.subscription-overdue';
      }
    } else if (
      userLicence?.licenseType === LicenseType.BLOCKED &&
      userLicence?.licenceExpireDate &&
      new Date(userLicence.licenceExpireDate) < new Date()
    ) {
      tagConfig = { ...tagConfig, bgcolor: '#D13131' };
      tagKey = 'navigation.subscription-locked';
    }

    return (
      <ColoredTag
        color="#FFFFFF"
        bgcolor={tagConfig.bgcolor}
        style={{
          marginInlineEnd: 0,
          padding: tagConfig.padding,
          marginBottom: tagConfig.marginBottom,
          textAlign: 'center',
          fontSize: tagConfig.fontSize,
          fontWeight: tagConfig.fontWeight,
        }}
      >
        {t(tagKey)}
      </ColoredTag>
    );
  };

  const trialExpired =
    userLicence?.licenseType === LicenseType.TRIAL &&
    userLicence?.licenseDaysLeft === 0;

  const showSubscription =
    !!currentUser &&
    (AccountType[currentUser.accountType] === AccountType.OWNER ||
      AccountType[currentUser.accountType] === AccountType.STAFF) &&
    userLicence?.status !== LicenseStatus.UNLIMITED;

  const showTrialFooter =
    userLicence?.licenseType === LicenseType.TRIAL &&
    userLicence?.status !== LicenseStatus.UNLIMITED;

  const trialUrgent = (userLicence?.licenseDaysLeft ?? 0) <= 3;

  const renderMenuActions = (close: () => void) => (
    <>
      <AppUserMenuAction
        disabled={trialExpired}
        onClick={() => {
          if (trialExpired) return;
          navigate(routes.account.root);
          close();
        }}
        icon={<IconUsers style={USER_MENU_ICON_STYLE} />}
        label={t('navigation.profile')}
        extra={
          !compactHeader &&
          userLicence?.licenseType === LicenseType.TRIAL &&
          userLicence?.status !== LicenseStatus.UNLIMITED &&
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.OWNER ? (
            <ScopeButton
              type="primary"
              height="24px"
              width="80px"
              fontSize="12px"
              lineheight="12px"
              style={{ marginInlineStart: 'auto', flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  userLicence?.licenseType === LicenseType.PAID
                    ? routes.billing.root
                    : routes.pricing.root
                );
                close();
              }}
            >
              {t('navigation.upgrade')}
            </ScopeButton>
          ) : undefined
        }
      />

      {!compactHeader &&
        currentUser &&
        AccountType[currentUser.accountType] === AccountType.OWNER &&
        userLicence?.status !== LicenseStatus.UNLIMITED && (
          <AppUserMenuAction
            onClick={() => {
              navigate(routes.billing.root);
              close();
            }}
            icon={<IconBilling style={USER_MENU_ICON_STYLE} />}
            label={t('navigation.billing')}
          />
        )}

      {!compactHeader && (
        <AppUserMenuAction
          onClick={() => {
            showReviewModal();
            close();
          }}
          icon={<StarOutlined style={antIconStyle} />}
          label={t('navigation.leave-a-review')}
        />
      )}

      <AppUserMenuAction
        onClick={() => {
          navigate(routes.accessibility);
          close();
        }}
        icon={<FontSizeOutlined style={antIconStyle} />}
        label={t('navigation.accessibility')}
      />

      {!compactHeader && !multipleLanguagesDisabled && (
        <AppUserMenuAction
          className="user-menu-language"
          onClick={(e) => e.stopPropagation()}
        >
          <LanguageSelector
            menuSize={MenuSize.WIDE}
            variant="user-menu"
            onOpenChange={setLanguageMenuOpen}
          />
        </AppUserMenuAction>
      )}

      <AppUserMenuAction
        onClick={() => {
          dispatch(logoutRequest());
        }}
        icon={<IconLogout style={USER_MENU_ICON_STYLE} />}
        label={t('navigation.logout')}
      />
    </>
  );

  const avatar = currentUser
    ? renderAvatar(
        currentUser?.firstName,
        currentUser?.lastName,
        AvatarSizes.DEFAULT,
        currentUser?.userUuid,
        userProfilePicture,
        'right',
        true
      )
    : undefined;

  return (
    <AppUserMenu
      size={menuSize === MenuSize.WIDE ? 'wide' : 'narrow'}
      loading={userLoading}
      displayName={userName}
      ariaLabel={
        currentUser
          ? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() ||
            t('common.account')
          : t('common.account')
      }
      avatar={avatar}
      subtitle={
        embedInHeader
          ? undefined
          : showSubscription
            ? renderUserSubscription()
            : undefined
      }
      holdOpen={languageMenuOpen}
      onOpenChange={(open) => {
        if (!open) setLanguageMenuOpen(false);
      }}
      belowButton={
        showTrialFooter ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 1,
            }}
          >
            {!isNarrow && (
              <Progress
                type="circle"
                strokeColor={
                  trialUrgent
                    ? 'var(--color-status-danger, #D13131)'
                    : 'var(--color-primary, #41aaaf)'
                }
                style={{ marginInlineEnd: 5 }}
                percent={((userLicence!.licenseDaysLeft / 30) * 100 - 100) * -1}
                showInfo={false}
                size={20}
              />
            )}
            <span
              style={{
                color: trialUrgent
                  ? 'var(--color-status-danger, #D13131)'
                  : 'var(--color-primary, #41aaaf)',
                fontSize: isNarrow ? 10 : 12,
              }}
            >
              {t('navigation.trial-days-left', {
                count: userLicence?.licenseDaysLeft,
              })}
            </span>
          </div>
        ) : embedInHeader ? (
          null
        ) : (
          undefined
        )
      }
    >
      {({ close }) => renderMenuActions(close)}
    </AppUserMenu>
  );
};
